import React, { useContext } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon
} from '@clientbase/clientbase-library'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { InvoiceItem } from 'models/Invoice'

import { Table } from 'components/Table'
import ChipTag from 'components/Tag'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import { ModalChargeDetailContext } from '../../context'
import { cardTittle } from '../../ModalChargeDetail.utils'

// Tag configurations for MRR and AVULSA
const tagConfig = {
  mrr: {
    label: 'MRR',
    tooltip: 'Este item é contabilizado na Receita Recorrente Mensal (MRR)',
    color: '#1976d2',
    bgColor: 'rgba(25, 118, 210, 0.1)'
  },
  avulsa: {
    label: 'AVULSA',
    tooltip: 'Este item é contabilizado como Receita Avulsa',
    color: '#ff9800',
    bgColor: 'rgba(255, 152, 0, 0.1)'
  }
}

// Nomes de tag que já são representados pelo chip métrico. Tags com esses nomes
// não são repetidas na lista de tags do item — senão a linha mostra "MRR" duas vezes.
const METRIC_TAG_NAMES = ['MRR', 'AVULSA', 'AVULSO']

const tagName = (t: any) =>
  (typeof t === 'string' ? t : t?.name || '').toUpperCase()

// Classificação MRR x avulsa do item (SC-11024). O backend manda pronta em
// `metric_kind` (BillingItem#metric_kind), que é a MESMA regra do MRR das
// Métricas: o badge mostra exatamente o que o número conta. O cálculo abaixo é
// só o espelho dela para quando o campo não vier (payload antigo/cache):
//   1. tag do item  2. tag do plano  (AVULSA/AVULSO > MRR em cada um)
//   3. padrão: em recorrência é MRR, exceto item de 1 ciclo; fora, avulsa
const metricKindFromTags = (tags: any[] = []): 'mrr' | 'avulsa' | null => {
  const upper = tags.map(tagName)
  if (upper.includes('AVULSA') || upper.includes('AVULSO')) return 'avulsa'
  if (upper.includes('MRR')) return 'mrr'
  return null
}

export const getItemTagFromProduct = (
  item: InvoiceItem,
  hasRecurrence: boolean
): 'mrr' | 'avulsa' => {
  if (item.metric_kind === 'mrr' || item.metric_kind === 'avulsa') {
    return item.metric_kind
  }

  const explicit =
    metricKindFromTags(item.tags) ||
    metricKindFromTags(item.product?.tags as any[])
  if (explicit) return explicit

  if (!hasRecurrence) return 'avulsa'

  const window =
    Number(item.total_cycles) ||
    Number((item.product as any)?.total_cycles) ||
    0
  return window === 1 ? 'avulsa' : 'mrr'
}

// Tags próprias do item (as que o merchant configurou na composição). São
// diferentes do chip métrico MRR/AVULSA — vêm do backend como array de objetos
// e podem ter só `uuid` em vez de `id`, então normalizamos como em utils/tags.
const renderItemTags = (item: InvoiceItem) => {
  const tags = (item.tags || [])
    .map((tag) => ({
      id: tag.id || tag.uuid || tag.name,
      name: tag.name,
      color: tag.color
    }))
    .filter((tag) => !!tag.name)
    // O chip métrico já exibe MRR/AVULSA; repetir aqui duplicaria a informação.
    .filter((tag) => !METRIC_TAG_NAMES.includes(tagName(tag)))

  if (tags.length === 0) return null

  const estilo = {
    height: 20,
    fontSize: '0.6875rem',
    '& .MuiChip-label': { px: '8px' }
  }

  // Acima de duas, as tags empurravam as colunas de valor para fora e criavam
  // scroll horizontal na tabela. Mostramos as duas primeiras e resumimos o
  // resto num "+N", com os nomes completos no tooltip.
  const VISIVEIS = 2
  const visiveis = tags.slice(0, VISIVEIS)
  const ocultas = tags.slice(VISIVEIS)

  return (
    <>
      {visiveis.map((tag) => (
        <ChipTag key={tag.id} tag={tag} sx={estilo} />
      ))}
      {ocultas.length > 0 && (
        <Tooltip
          title={
            // Uma por linha: com nomes longos, a lista separada por ponto
            // virava um bloco de texto corrido difícil de varrer.
            <Stack gap="2px">
              {ocultas.map((tag) => (
                <span key={tag.id}>{tag.name}</span>
              ))}
            </Stack>
          }
        >
          {/* span porque o Chip não encaminha os handlers do tooltip.
              O `preventDefault` é por causa do <a> que envolve a linha quando o
              item tem plano: sem ele, tocar o "+N" para ler o tooltip abria o
              plano em outra guia. */}
          <span
            style={{ display: 'inline-flex' }}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
          >
            <Chip label={`+${ocultas.length}`} size="small" sx={estilo} />
          </span>
        </Tooltip>
      )}
    </>
  )
}

// Helper to render metric tag chip
const renderMetricTag = (item: InvoiceItem, hasRecurrence: boolean) => {
  // Tag is determined by product tags first, then billing type as fallback
  const tag = getItemTagFromProduct(item, hasRecurrence)

  const config = tagConfig[tag]
  return (
    <Tooltip enterTouchDelay={0} arrow placement="top" title={config.tooltip}>
      <Chip
        label={config.label}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.625rem',
          fontWeight: 700,
          bgcolor: config.bgColor,
          color: config.color,
          '& .MuiChip-label': {
            px: 1
          }
        }}
      />
    </Tooltip>
  )
}

// Bonificação configurada no item, em R$. Espelha BillingItem#bonus_value,
// inclusive o teto no valor do item.
export const getItemBonusValue = (item: InvoiceItem | any): number => {
  const policy = (item as any)?.bonus_policy
  const amount = Number((item as any)?.bonus_amount) || 0
  if (!policy || policy === 'no_bonus' || amount <= 0) return 0

  const total = Number(item?.amount_billed) || 0
  const value = policy === 'percentage' ? (total * amount) / 100 : amount

  return Math.min(value, total)
}

// Janela da bonificação do item (bonus_start_cycle / bonus_total_cycles), mesma regra
// da bonificação geral: ciclos da recorrência, total 0 = até o fim. Vazio quando é o
// default (vale em todo ciclo), que é o caso de toda cobrança e da maioria dos itens.
export const renderItemBonusWindow = (item: InvoiceItem | any) => {
  const start = Math.max(Number((item as any)?.bonus_start_cycle) || 1, 1)
  const total = Math.max(Number((item as any)?.bonus_total_cycles) || 0, 0)

  if (start <= 1 && total === 0) return ''
  if (total === 0) return `a partir do ciclo ${start}`
  if (total === 1) return `ciclo ${start}`

  return `ciclos ${start} a ${start + total - 1}`
}

export const renderItemBonus = (item: InvoiceItem | any) => {
  const value = getItemBonusValue(item)
  if (value <= 0) return '-'

  const policy = (item as any)?.bonus_policy
  const amount = Number((item as any)?.bonus_amount) || 0

  const base =
    policy === 'percentage'
      ? `${amount}% (${formatMoney(value)})`
      : formatMoney(value)
  const window = renderItemBonusWindow(item)

  return window ? `${base} · ${window}` : base
}

// Total da linha já com a bonificação do item abatida. A bonificação é incondicional
// e reduz o amount_billed da cobrança, então a composição reconcilia com o valor de face.
export const renderItemTotal = (item: InvoiceItem | any) => {
  const total = Number(item?.amount_billed) || 0
  return formatMoney(total - getItemBonusValue(item))
}

// Janela de ciclos do item (start_cycle / total_cycles). Mesma semântica do editor
// (ItemRow): total_cycles = 0 significa "até o fim da recorrência". Janela efetiva:
// [start_cycle, start_cycle + total_cycles - 1].
export const renderItemCycles = (item: InvoiceItem | any) => {
  const start = Number(item?.start_cycle) || 1
  // Item sem janela própria (total_cycles = 0) herda o teto do produto, igual ao
  // backend (Stateful.filter_billing_items_by_cycle). Sem isso um setup de produto
  // "1x" aparecia como "Todos os ciclos", embora só seja cobrado uma vez (SC-10971).
  const total =
    Number(item?.total_cycles) || Number(item?.product?.total_cycles) || 0
  // Cadência do item: com passo > 1 a janela deixa de ser contígua, e total_cycles conta
  // COBRANÇAS, não ciclos da recorrência. Sem isso o detalhe mostraria "Ciclos 1 a 4" para
  // um item trimestral que na verdade é cobrado até o ciclo 10.
  const interval = Math.max(Number(item?.cycle_interval) || 1, 1)

  const every = interval > 1 ? ` (a cada ${interval} ciclos)` : ''

  if (start <= 1 && total === 0) {
    return interval > 1 ? `A cada ${interval} ciclos` : 'Todos os ciclos'
  }
  if (total === 0) return `A partir do ciclo ${start}${every}`

  if (total === 1) return `Ciclo ${start}`

  // Com passo > 1 a janela NÃO é contígua: um item trimestral de 5 cobranças a
  // partir do ciclo 3 cai nos ciclos 3, 6, 9, 12 e 15. Dizer "Ciclos 3 a 15"
  // sugeriria cobrança em todos os ciclos do intervalo, então informamos
  // quantas vezes e de onde parte — o fim é derivável e atrapalha mais do que
  // ajuda. O `every` já está implícito em "5x", e repeti-lo aqui espremia a
  // coluna a ponto de quebrar em quatro linhas.
  if (interval > 1) {
    return `${total}x a partir do ciclo ${start}`
  }

  return `Ciclos ${start} a ${start + total - 1}`
}

/**
 * Nome do item em uma linha só, com o texto inteiro no tooltip.
 *
 * A linha da composição tem nome + tags + ciclos disputando a mesma largura;
 * deixar o nome quebrar empurrava as tags e o "(5x)" para uma segunda linha e
 * desalinhava a tabela. Truncar mantém a linha estável, e o tooltip garante que
 * nada fica inacessível.
 */
const ItemName = ({ children }: { children: string }) => (
  <Tooltip title={children}>
    <Typography
      sx={{
        // Sem maxWidth fixo: com um teto em pixels o nome truncava mesmo
        // sobrando espaço até a coluna "Valor". `flex: 1` faz o nome tomar toda
        // a folga da célula e `minWidth: 0` permite que ele encolha (e só então
        // trunque) quando as tags e o "(5x)" precisarem do espaço.
        // `flex: 0 1 auto` em vez de `flex: 1`: o nome ocupa o que precisa e
        // encolhe (truncando) só quando falta espaço. Com `flex: 1` ele tomava
        // toda a folga da célula e empurrava as tags para longe.
        flex: '0 1 auto',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </Typography>
  </Tooltip>
)

export const compositionDetailMapper = (
  expenseComposition: InvoiceItem[],
  hasRecurrence: boolean,
  /**
   * A tabela monta as células a partir das CHAVES da linha, não do header.
   * Então esconder a coluna no header sem omitir a chave aqui desloca todos os
   * valores seguintes — "Qtd." passava a exibir o traço da bonificação.
   */
  showBonus = true,
  /** Idem: sem a coluna 'cycles' no header, a chave sobra como célula órfã. */
  showCycles = true
) => {
  return expenseComposition?.map((item) => ({
    item: item.product ? (
      <a
        target="_blank"
        href={`/planos/${item.product.uuid}`}
        // `all: unset` zera o display também; sem reconstruí-lo o link vira
        // inline e não respeita o alinhamento da Stack interna.
        style={{
          all: 'unset',
          display: 'inline-flex',
          minWidth: 0,
          cursor: 'pointer'
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="nowrap"
          gap={1}
          // justifyContent explícito: sem ele a célula distribuía a sobra e as
          // tags flutuavam longe do nome, coladas na coluna "Valor".
          justifyContent="flex-start"
        >
          <ItemName>{item.product.name}</ItemName>
          <Tooltip title="Abrir plano em nova guia">
            {/* span porque o Icon não encaminha os handlers do tooltip.
                Mesmo tamanho e cor do ícone que abre o cliente na faixa do
                pagador, para os dois lerem como a mesma ação. */}
            <span style={{ display: 'inline-flex', marginLeft: '-2px' }}>
              <Icon
                icon="open"
                sx={{ fontSize: '15px' }}
                colorSX={theme.palette.neutral[400]}
              />
            </span>
          </Tooltip>
          {renderMetricTag(item, hasRecurrence)}
          {renderItemTags(item)}
          <Typography color={theme.palette.neutral[400]} fontWeight={600}>
            {(item.product as any).total_cycles == 0 ? (
              <Icon icon="cached" colorSX={theme.palette.neutral[400]} />
            ) : (
              `(${(item.product as any).total_cycles}x)`
            )}
          </Typography>
        </Stack>
      </a>
    ) : (
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="nowrap"
        gap={1}
        justifyContent="flex-start"
      >
        <ItemName>{item.description || '-'}</ItemName>
        {renderMetricTag(item, hasRecurrence)}
        {renderItemTags(item)}
      </Stack>
    ),
    amountUnit: formatMoney(item?.amount_unit) || '',
    ...(showBonus ? { bonus: renderItemBonus(item) } : {}),
    quantity: item?.quantity || '',
    amountBilled: renderItemTotal(item),
    // Só na tabela do detalhe de recorrência. A de cobrança não tem a coluna
    // 'cycles' no header, e como o Table monta as células pelas CHAVES da
    // linha, a chave extra virava uma coluna sem cabeçalho ("Todos os ciclos"
    // solto à direita de "Total").
    ...(showCycles ? { cycles: renderItemCycles(item) } : {})
  }))
}

const Composition: React.FC = () => {
  const { invoice } = useContext(ModalChargeDetailContext)
  const hasRecurrence = !!invoice.recurrenceUuid

  // Mesma regra de `renderItemBonus`: só conta bonificação que resulta em
  // valor. Sem nenhum item bonificado a coluna era uma fileira de traços.
  const hasAnyItemBonus = (invoice?.items || []).some(
    (item) => getItemBonusValue(item) > 0
  )

  return (
    <Accordion
      defaultExpanded
      sx={{
        border: '1px solid #E8EBE8',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderRadius: '8px !important',
        backgroundColor: '#FAFBFA',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          backgroundColor: '#fff'
        }
      }}
    >
      <AccordionSummary aria-controls="composition-content" id="composition">
        {cardTittle(
          'Composição da cobrança',
          'paid',
          `${theme.palette.primary.main}`
        )}
      </AccordionSummary>
      <AccordionDetails>
        <div>
          <Box maxHeight="200px" overflow="auto">
            <Table
              mobileCards
              header={[
                { id: 'item', label: 'Plano/Item' },
                { id: 'amountUnit', label: 'Valor' },
                ...(hasAnyItemBonus
                  ? [{ id: 'bonus', label: 'Bonificação' }]
                  : []),
                { id: 'quantity', label: 'Qtd.' },
                { id: 'amountBilled', label: 'Total' }
              ]}
              hoverInRow
              rows={compositionDetailMapper(
                invoice.items,
                hasRecurrence,
                hasAnyItemBonus,
                // A tabela da cobrança não tem a coluna de ciclos.
                false
              )}
              select={{ useSelect: false }}
              hiddePagination
              hideLastRowSeparator
            />
          </Box>
        </div>
      </AccordionDetails>
    </Accordion>
  )
}

export default Composition
